
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Member } from "@/types/member";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { DollarSign, ChevronDown } from "lucide-react";

interface ExpenseFormProps {
  groupId: string;
  members: Member[];
  currencies: { code: string; symbol: string }[];
  showExpenseForm: boolean;
  editingExpenseId: string | null;
  initialExpense: {
    title: string;
    amount: string;
    currency: string;
    paidBy: string;
    sharedWith: string[];
  };
  onCancel: () => void;
  onSubmit: (expense: {
    title: string;
    amount: string;
    currency: string;
    paidBy: string;
    sharedWith: string[];
  }) => Promise<void>;
}

export const ExpenseForm = ({
  groupId,
  members,
  currencies,
  showExpenseForm,
  editingExpenseId,
  initialExpense,
  onCancel,
  onSubmit
}: ExpenseFormProps) => {
  const { t } = useTranslation();
  const [expenseTitle, setExpenseTitle] = useState(initialExpense.title);
  const [expenseAmount, setExpenseAmount] = useState(initialExpense.amount);
  const [selectedCurrency, setSelectedCurrency] = useState(initialExpense.currency);
  const [paidBy, setPaidBy] = useState(initialExpense.paidBy);
  const [selectedMembers, setSelectedMembers] = useState<string[]>(initialExpense.sharedWith);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const { toast } = useToast();

  // Find the symbol for the selected currency
  const selectedCurrencySymbol = currencies.find(c => c.code === selectedCurrency)?.symbol || '';

  useEffect(() => {
    const fetchCurrentUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setCurrentUser(session.user.id);
      }
    };
    
    fetchCurrentUser();
  }, []);
  
  useEffect(() => {
    setExpenseTitle(initialExpense.title);
    setExpenseAmount(initialExpense.amount);
    setSelectedCurrency(initialExpense.currency);
    setPaidBy(initialExpense.paidBy || currentUser || "");
    setSelectedMembers(initialExpense.sharedWith);
  }, [initialExpense, currentUser]);

  const handleSubmit = async () => {
    if (!expenseTitle || !expenseAmount || !paidBy || selectedMembers.length === 0) {
      toast({
        title: t('common.error'),
        description: t('expenses.missingFields'),
        variant: "destructive"
      });
      return;
    }

    try {
      await onSubmit({
        title: expenseTitle,
        amount: expenseAmount,
        currency: selectedCurrency,
        paidBy,
        sharedWith: selectedMembers
      });
      
      setExpenseTitle("");
      setExpenseAmount("");
      setPaidBy("");
      setSelectedMembers([]);
      
      onCancel();
    } catch (error) {
      console.error("Error in expense form submit:", error);
    }
  };

  if (!showExpenseForm) {
    return null;
  }

  // Group currencies by region/popularity for better organization
  const popularCurrencies = ['USD', 'EUR', 'JPY', 'GBP', 'CNY'];
  const sortedCurrencies = [...currencies].sort((a, b) => {
    // Put popular currencies first
    const aPopular = popularCurrencies.includes(a.code);
    const bPopular = popularCurrencies.includes(b.code);
    
    if (aPopular && !bPopular) return -1;
    if (!aPopular && bPopular) return 1;
    
    // Then alphabetically
    return a.code.localeCompare(b.code);
  });

  return (
    <Card className="p-3 space-y-3 bg-white/50 dark:bg-black/50 mt-2">
      <div className="space-y-2">
        <Label htmlFor="expense-title">{t('expenses.formTitle')}</Label>
        <Input 
          id="expense-title"
          placeholder={t('expenses.expenseTitle')} 
          value={expenseTitle} 
          onChange={e => setExpenseTitle(e.target.value)} 
        />
      </div>
      
      <div className="flex gap-2">
        <div className="flex-1 space-y-2">
          <Label htmlFor="expense-amount">{t('expenses.amount')}</Label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span className="text-gray-500 dark:text-gray-400">{selectedCurrencySymbol}</span>
            </div>
            <Input 
              id="expense-amount"
              type="number" 
              placeholder={t('expenses.amount')} 
              value={expenseAmount} 
              onChange={e => setExpenseAmount(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="currency-select">{t('expenses.currency')}</Label>
          <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
            <SelectTrigger id="currency-select" className="w-[90px] md:w-[100px]">
              <SelectValue placeholder={t('expenses.currency')} />
            </SelectTrigger>
            <SelectContent 
              position="popper" 
              className="max-h-[300px] overflow-y-auto"
              align="end"
            >
              {sortedCurrencies.map(currency => (
                <SelectItem 
                  key={currency.code} 
                  value={currency.code}
                  className="flex items-center gap-2 py-2"
                >
                  <span className="inline-flex items-center gap-1 min-w-[60px]">
                    <span className="text-muted-foreground">{currency.symbol}</span>
                    <span>{currency.code}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="paid-by-select">{t('expenses.whoPaid')}</Label>
        <Select value={paidBy} onValueChange={setPaidBy}>
          <SelectTrigger id="paid-by-select" className="w-full h-12 text-left">
            <SelectValue placeholder={t('expenses.whoPaid')} />
          </SelectTrigger>
          <SelectContent position="popper" className="w-[--radix-select-trigger-width] max-h-[300px]">
            {members.map(member => (
              <SelectItem key={member.id} value={member.id} className="py-3 px-4 cursor-pointer hover:bg-accent">
                <div className="flex items-center gap-2">
                  <Avatar className="h-5 w-5">
                    <AvatarImage src={member.avatar_url || undefined} />
                    <AvatarFallback>{member.name.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{member.name}</span>
                    <span className="text-xs text-gray-500">@{member.username}</span>
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <h3 className="text-sm font-medium">{t('expenses.splitWith')}</h3>
        <div className="grid grid-cols-1 gap-2">
          {members.map(member => (
            <div key={member.id} className="flex items-center gap-2 bg-white/30 dark:bg-black/30 p-3 rounded">
              <input 
                type="checkbox" 
                id={`member-${member.id}`} 
                checked={selectedMembers.includes(member.id)} 
                onChange={e => {
                  if (e.target.checked) {
                    setSelectedMembers([...selectedMembers, member.id]);
                  } else {
                    setSelectedMembers(selectedMembers.filter(id => id !== member.id));
                  }
                }} 
                className="rounded w-5 h-5 cursor-pointer" 
              />
              <label 
                htmlFor={`member-${member.id}`} 
                className="flex flex-1 items-center gap-2 cursor-pointer py-1"
              >
                <Avatar className="h-5 w-5">
                  <AvatarImage src={member.avatar_url || undefined} />
                  <AvatarFallback>{member.name.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{member.name}</span>
                  <span className="text-xs text-gray-500">@{member.username}</span>
                </div>
              </label>
            </div>
          ))}
        </div>
      </div>
      
      <div className="flex gap-2 pt-2">
        <Button variant="outline" size="mobile" onClick={onCancel} className="flex-1">
          {t('groups.cancel')}
        </Button>
        <Button onClick={handleSubmit} size="mobile" className="flex-1">
          {editingExpenseId ? t('expenses.updateExpense') : t('expenses.addExpense')}
        </Button>
      </div>
    </Card>
  );
};
